// app/components/Connectors/binance/OrderBook.tsx:
'use client'

import React, { useEffect, useState, useRef } from 'react'
import styles from './OrderBook.module.css'
import { throttle } from 'lodash' // Ensure lodash.throttle is installed
import { usePathname } from 'next/navigation'
import {
  ChevronDownIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline'

// --------------------------
// Type Declarations
// --------------------------
interface OrderItem {
  price: number
  size: number
}

interface AggregatedOrderItem {
  price: number
  size: number
  count: number
}

export interface OrderBookData {
  bids: OrderItem[]
  asks: OrderItem[]
}

// --------------------------
// OrderBookInner Component
// --------------------------
// This component handles the live data via WebSocket and renders the order book.
// IMPORTANT: It receives a key based on the symbol so that it fully remounts when the symbol changes.
interface OrderBookInnerProps {
  symbol: string
  stepSize: number
}

function OrderBookInner({ symbol, stepSize }: OrderBookInnerProps) {
  // State for raw order book data
  const [data, setData] = useState<OrderBookData>({ bids: [], asks: [] })
  // State for aggregated data (to render)
  const [aggregatedData, setAggregatedData] = useState<{
    bids: AggregatedOrderItem[]
    asks: AggregatedOrderItem[]
  }>({ bids: [], asks: [] })

  // Refs to manage scroll positions
  const asksListRef = useRef<HTMLDivElement>(null)
  const bidsListRef = useRef<HTMLDivElement>(null)

  // --------------------------
  // WebSocket Connection Effect
  // --------------------------
  useEffect(() => {
    // HELP: Clear previous data on mount so we start fresh.
    setData({ bids: [], asks: [] })

    // Create a throttled function to update data
    const throttledSetData = throttle((newData: OrderBookData) => {
      setData(newData)
    }, 100)

    let ws: WebSocket | null = null
    let reconnectTimer: NodeJS.Timeout | null = null
    let reconnectAttempts = 0
    let isConnected = false

    const connectWebSocket = () => {
      if (isConnected) return

      try {
        ws = new WebSocket(
          `wss://stream.binance.com:9443/ws/${symbol.toLowerCase()}@depth20@100ms`
        )

        ws.onopen = () => {
          console.log('WebSocket connected for symbol:', symbol)
          reconnectAttempts = 0
          isConnected = true
        }

        ws.onmessage = event => {
          try {
            const messageData = JSON.parse(event.data)
            if (!messageData.bids || !messageData.asks) return

            const newData: OrderBookData = {
              bids: messageData.bids.map((bid: string[]) => ({
                price: parseFloat(bid[0]),
                size: parseFloat(bid[1])
              })),
              asks: messageData.asks.map((ask: string[]) => ({
                price: parseFloat(ask[0]),
                size: parseFloat(ask[1])
              }))
            }

            throttledSetData(newData)
          } catch (error) {
            console.error('Error parsing data:', error)
          }
        }

        ws.onerror = err => {
          console.error('WebSocket error:', err)
          isConnected = false
        }

        ws.onclose = () => {
          isConnected = false
          if (reconnectTimer) clearTimeout(reconnectTimer)
          const delay = Math.min(10000, 1000 * Math.pow(2, reconnectAttempts))
          reconnectTimer = setTimeout(() => {
            reconnectAttempts++
            connectWebSocket()
          }, delay)
        }
      } catch (error) {
        console.error('WebSocket connection error:', error)
      }
    }

    connectWebSocket()

    return () => {
      // HELP: On cleanup, remove all handlers and close the WebSocket.
      if (ws) {
        ws.onmessage = null
        ws.onerror = null
        ws.onclose = null
        ws.close(1000, 'Symbol changed')
      }
      if (reconnectTimer) clearTimeout(reconnectTimer)
      throttledSetData.cancel()
    }
  }, [symbol])

  // --------------------------
  // Data Aggregation Effect
  // --------------------------
  useEffect(() => {
    const aggregateOrders = (orders: OrderItem[]): AggregatedOrderItem[] => {
      const aggregated: Record<number, { size: number; count: number }> = {}
      orders.forEach(order => {
        // HELP: Round price down to the nearest step size.
        const roundedPrice = Math.floor(order.price / stepSize) * stepSize
        if (aggregated[roundedPrice]) {
          aggregated[roundedPrice].size += order.size
          aggregated[roundedPrice].count += 1
        } else {
          aggregated[roundedPrice] = { size: order.size, count: 1 }
        }
      })
      return Object.entries(aggregated).map(([price, { size, count }]) => ({
        price: parseFloat(price),
        size,
        count
      }))
    }

    let aggregatedBids = aggregateOrders(data.bids).sort(
      (a, b) => b.price - a.price
    )
    let aggregatedAsks = aggregateOrders(data.asks).sort(
      (a, b) => a.price - b.price
    )
    const LIMIT = 10
    // HELP: Limit both sides to the same number of rows.
    const minLength = Math.min(
      aggregatedBids.length,
      aggregatedAsks.length,
      LIMIT
    )
    aggregatedBids = aggregatedBids.slice(0, minLength)
    aggregatedAsks = aggregatedAsks.slice(0, minLength)
    // HELP: Pad with empty entries if needed.
    const padList = (
      list: AggregatedOrderItem[],
      limit: number
    ): AggregatedOrderItem[] => {
      const padded = [...list]
      while (padded.length < limit) {
        padded.push({ price: 0, size: 0, count: 0 })
      }
      return padded
    }
    aggregatedBids = padList(aggregatedBids, LIMIT)
    aggregatedAsks = padList(aggregatedAsks, LIMIT)

    setAggregatedData({ bids: aggregatedBids, asks: aggregatedAsks })

    // HELP: Optionally scroll the asks section to the bottom.
    if (asksListRef.current) {
      asksListRef.current.scrollTop = asksListRef.current.scrollHeight
    }
  }, [data, stepSize])

  // Determine maximum counts for background bar scaling.
  const maxBidCount = Math.max(...aggregatedData.bids.map(bid => bid.count), 1)
  const maxAskCount = Math.max(...aggregatedData.asks.map(ask => ask.count), 1)

  return (
    <div className={styles.mainContent}>
      {/* RENDER ASKS */}
      <div className={styles.asksSection} ref={asksListRef}>
        {aggregatedData.asks.map((ask, idx) => {
          const widthPercent = (ask.count / maxAskCount) * 100
          return (
            <div
              key={idx}
              className={styles.orderRow}
              title={`Price: ${ask.count > 0 ? ask.price.toFixed(2) : '-'}, Size: ${ask.count > 0 ? ask.size.toFixed(2) : '-'}, Orders: ${ask.count > 0 ? ask.count : '-'}`}
            >
              <div
                className={`${styles.backgroundBar} ${styles.askBar}`}
                style={{ width: `${widthPercent}%` }}
              ></div>
              <div className={styles.priceText}>
                {ask.count > 0 ? ask.price.toFixed(2) : '-'}
              </div>
              <div className={styles.amountText}>
                {ask.count > 0 ? (ask.price * ask.size).toFixed(2) : '-'}
              </div>
            </div>
          )
        })}
      </div>

      {/* Separator with Best Ask/Bid */}
      <div className={styles.separatorContainer}>
        <div className={styles.bestAsk}>
          {aggregatedData.asks[0]?.count > 0
            ? aggregatedData.asks[0].price.toFixed(2)
            : 'N/A'}
        </div>
        <div className={styles.separator}></div>
        <div className={styles.bestBid}>
          {aggregatedData.bids[0]?.count > 0
            ? aggregatedData.bids[0].price.toFixed(2)
            : 'N/A'}
        </div>
      </div>

      {/* RENDER BIDS */}
      <div className={styles.bidsSection} ref={bidsListRef}>
        {aggregatedData.bids.map((bid, idx) => {
          const widthPercent = (bid.count / maxBidCount) * 100
          return (
            <div
              key={idx}
              className={styles.orderRow}
              title={`Price: ${bid.count > 0 ? bid.price.toFixed(2) : '-'}, Size: ${bid.count > 0 ? bid.size.toFixed(2) : '-'}, Orders: ${bid.count > 0 ? bid.count : '-'}`}
            >
              <div
                className={`${styles.backgroundBar} ${styles.bidBar}`}
                style={{ width: `${widthPercent}%` }}
              ></div>
              <div className={styles.priceText}>
                {bid.count > 0 ? bid.price.toFixed(2) : '-'}
              </div>
              <div className={styles.amountText}>
                {bid.count > 0 ? (bid.price * bid.size).toFixed(2) : '-'}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// --------------------------
// Parent OrderBook Component
// --------------------------
// This component manages the symbol selector and step‑size controls.
// It renders OrderBookInner with a key tied to the current symbol so that a change forces a remount.
export default function OrderBook() {
  const pathname = usePathname()
  // Extract the first segment after the domain from the URL path
  const pathSegments = pathname ? pathname.split('/').filter(Boolean) : []
  const symbolFromPath = pathSegments[0] || 'btcusdt'

  // HELP: If the symbol ends with .NS or .BS, use a fallback symbol.
  const symbolToUse =
    symbolFromPath.toUpperCase().endsWith('.NS') ||
    symbolFromPath.toUpperCase().endsWith('.BS')
      ? 'btcusdt'
      : symbolFromPath

  // State for symbol selection and custom input.
  const [currentSymbol, setCurrentSymbol] = useState(symbolToUse)
  const [isSymbolPopupOpen, setIsSymbolPopupOpen] = useState(false)
  const [symbolInput, setSymbolInput] = useState('')
  const [popularSymbols] = useState([
    'BTCUSDT',
    'ETHUSDT',
    'BNBUSDT',
    'SOLUSDT',
    'LTCUSDT'
  ])
  // State for step‑size.
  const [stepSize, setStepSize] = useState<number>(1)

  const handleSymbolChange = (newSymbol: string) => {
    const formattedSymbol = newSymbol.toUpperCase()
    setCurrentSymbol(formattedSymbol)
    setIsSymbolPopupOpen(false)
    setSymbolInput('')
  }

  return (
    <div className={styles.orderBookContainer}>
      {/* Header with Title and Controls */}
      <div className={styles.header}>
        <span className={styles.title}>Order Book</span>
        <div className={styles.controls}>
          {/* Symbol Selector */}
          <div className={styles.symbolSelector}>
            <select
              value={currentSymbol}
              onChange={e => {
                if (e.target.value === 'custom') {
                  setIsSymbolPopupOpen(true)
                } else {
                  handleSymbolChange(e.target.value)
                }
              }}
              aria-label="Select Symbol"
              className={styles.symbolSelect}
            >
              <option value={currentSymbol}>
                {currentSymbol.toUpperCase()}
              </option>
              {popularSymbols.map(
                symbol =>
                  symbol !== currentSymbol.toUpperCase() && (
                    <option key={symbol} value={symbol}>
                      {symbol}
                    </option>
                  )
              )}
              <option value="custom">Custom</option>
            </select>
            {isSymbolPopupOpen && (
              <div className={styles.customSymbolPopup}>
                <input
                  type="text"
                  placeholder="Enter symbol (e.g. BTCUSDT)"
                  value={symbolInput}
                  onChange={e => setSymbolInput(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && symbolInput.trim()) {
                      handleSymbolChange(symbolInput.trim())
                    }
                  }}
                  className={styles.customSymbolInput}
                />
                <div className={styles.customSymbolButtons}>
                  <button
                    onClick={() => setIsSymbolPopupOpen(false)}
                    className={styles.cancelButton}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      if (symbolInput.trim()) {
                        handleSymbolChange(symbolInput.trim())
                      }
                    }}
                    className={styles.applyButton}
                    disabled={!symbolInput.trim()}
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>
          {/* Range Filter */}
          <div className={styles.rangeFilter}>
            <select
              id="stepSize"
              value={stepSize}
              onChange={e => setStepSize(Number(e.target.value))}
              aria-label="Select Price Step"
            >
              <option value={100}>100</option>
              <option value={10}>10</option>
              <option value={1}>1</option>
              <option value={0.1}>0.1</option>
            </select>
          </div>
        </div>
      </div>
      {/* 
          Using key={currentSymbol} forces a full remount of OrderBookInner
          when the symbol changes, so that no stale data persists.
      */}
      <OrderBookInner
        key={currentSymbol}
        symbol={currentSymbol}
        stepSize={stepSize}
      />
    </div>
  )
}
